/**
 * `return-path` — a one-shot stash for the `/connect/:request_id`
 * consent page so a signed-out visitor who follows the assistant's
 * authorize redirect can finish signing in and land back on the
 * right URL.
 *
 * Why localStorage, not a cookie: the magic-link bridge replaces the
 * cookie-less fetch on verify and the shortcut from Login redirects
 * to `/`, so the SPA bootstrap is where we recover. The stash is
 * only accepted for the `/connect/oar_<ULID>` surface the server
 * actually mints — nothing else routes to a view through this
 * mechanism, so a tampered value cannot land the user on an arbitrary
 * page.
 *
 * Shape in storage: `{ path, saved_at }` as JSON. 15-minute expiry
 * matches the server's `oauth_authorization_request` TTL (10 min)
 * with 5 min of signed-in-while-reading-email slack. Every read /
 * write is wrapped in try/catch — private browsing / disabled
 * storage must never throw.
 */

const STORAGE_KEY = 'sill_return_to';

/** Only accept the minted authorize-request URL shape. */
const CONNECT_PATH_RE = /^\/connect\/oar_[0-9A-HJKMNP-TV-Z]{26}$/;

/** 15 min — see module header. */
const EXPIRY_MS = 15 * 60 * 1000;

interface StoredReturn {
  readonly path: string;
  readonly saved_at: number;
}

/**
 * Record a return path for the signed-in bootstrap to consume. Silent
 * on any invalid path, storage failure or non-browser environment.
 */
export function saveReturnPath(path: string): void {
  if (typeof window === 'undefined') return;
  if (!CONNECT_PATH_RE.test(path)) return;
  try {
    const value: StoredReturn = { path, saved_at: Date.now() };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    /* storage disabled / quota — enhancement only */
  }
}

/**
 * Read and clear the saved return path. Returns `null` when nothing
 * is saved, the entry is older than {@link EXPIRY_MS}, the value
 * fails to parse, or the stored path no longer matches the allow-list
 * regex (defence in depth against a tampered localStorage value).
 *
 * Always clears the storage slot — even on invalid or expired
 * entries — so a stale cross-tab value never resurfaces.
 */
export function consumeReturnPath(): string | null {
  if (typeof window === 'undefined') return null;
  let raw: string | null;
  try {
    raw = window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
  if (raw === null) return null;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* best effort */
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (parsed === null || typeof parsed !== 'object') return null;
  const path = (parsed as { readonly path?: unknown }).path;
  const saved_at = (parsed as { readonly saved_at?: unknown }).saved_at;
  if (typeof path !== 'string' || typeof saved_at !== 'number') return null;
  if (!CONNECT_PATH_RE.test(path)) return null;
  if (!Number.isFinite(saved_at)) return null;
  if (Date.now() - saved_at > EXPIRY_MS) return null;
  return path;
}

/** Visible for tests — the regex the save/consume path enforces. */
export const CONNECT_RETURN_PATH_RE = CONNECT_PATH_RE;
/** Visible for tests. */
export const CONNECT_RETURN_PATH_STORAGE_KEY = STORAGE_KEY;
/** Visible for tests — how long a saved return path survives. */
export const CONNECT_RETURN_PATH_EXPIRY_MS = EXPIRY_MS;
